class ProductOptions extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        this.productIndex = this.dataset.productIndex ? parseInt(this.dataset.productIndex) : null;
        this.renderTargets = this.dataset.renderTargets ? this.dataset.renderTargets.split(',').map(target => target.trim()) : [];
        this.preselectOptions();
        this.addEventListener('change', this.onOptionChange);
    }

    getHiddenOptionNames() {
        return this.dataset.hiddenOptions
            ? this.dataset.hiddenOptions.split(',').map(s => s.trim()).filter(Boolean)
            : [];
    }

    getSelectedOptionValues() {
        // returns { "Color": "Black", "Size": "Large", ... }
        const out = {};
        this.querySelectorAll('.product-option').forEach(row => {
            const name = row.dataset.optionName;
            const checked = row.querySelector('input[type="radio"]:checked');
            if (name && checked) out[name] = checked.dataset.optionValue || '';
        });
        return out;
    }

    getVariants() {
        return this.getJson('.js-variants-json');
    }

    getVariantById(id) {
        return this.getVariants().find(v => v.id == id);
    }

    getProducts() {
        return this.getJson('.js-products-json');
    }

    getVariantMetafields() {
        return this.getJson('.js-variant-metafields-json');
    }

    getJson(selector) {
        const el = this.querySelector(selector);
        if (!el) return [];
        try { return JSON.parse(el.textContent || '[]'); } catch (e) { return []; }
    }

    findVariantByOptions(desiredOptionsByName) {
        const variants = this.getVariants();
        if (!variants.length) return null;

        const optionNamesInOrder = this.dataset.optionNames
            ? this.dataset.optionNames.split('|') // optional improvement if you want
            : null;

        return variants.find(v => {
            if (!v.available) return false;
            // Shopify variant has option1/2/3 but you have names. We match by comparing values in order:
            // We'll reconstruct option values based on this product’s option names in DOM order.
            const rows = Array.from(this.querySelectorAll('.product-option'));
            const values = rows.map((row, idx) => desiredOptionsByName[row.dataset.optionName] || null);

            // values[0] corresponds to option1, etc.
            if (values[0] && v.option1 !== values[0]) return false;
            if (values[1] && v.option2 !== values[1]) return false;
            if (values[2] && v.option3 !== values[2]) return false;
            return true;
        }) || null;
    }

    syncBundleChildrenToParent() {
        // Only the parent instance should drive this
        if (this.productIndex !== null) return;

        const parentSelected = this.getSelectedOptionValues();

        document.querySelectorAll('product-options[data-product-index]').forEach(child => {
            const hidden = child.getHiddenOptionNames?.() || [];
            if (!hidden.length) return;

            // child current selection provides non-controlled options
            const childSelected = child.getSelectedOptionValues?.() || {};

            // desired = child’s current selections, but override the hidden option names with parent values
            const desired = { ...childSelected };
            hidden.forEach(name => {
                if (parentSelected[name]) desired[name] = parentSelected[name];
            });

            const match = child.findVariantByOptions?.(desired);
            if (!match) return;

            // Switch bundle product to matching variant (and re-render its targets)
            child.updateUrl(match.id);
            child.updateFormValues(match.id);
            child.renderVariantUpdates(match.id);

            // Keep UI hidden for controlled options
            child.applyHiddenOptionsUI?.();
        });
    }


    preselectOptions() {
        const variantId = new URL(window.location.href).searchParams.get(this.productIndex ? `variant_${this.productIndex}` : 'variant');
        if (variantId) {
            this.updateFormValues(variantId);
            this.renderVariantUpdates(variantId);
            this.applyHiddenOptionsUI();
            this.applyPageValues(variantId);
            this.selectVariantImage(variantId);
            if (this.productIndex === null) this.syncBundleChildrenToParent();
            return;
        }

        const defaultVariant = this.dataset.defaultVariant;
        if (defaultVariant) {
            this.updateFormValues(defaultVariant);
            this.renderVariantUpdates(defaultVariant);
            this.applyPageValues(defaultVariant);
            this.selectVariantImage(defaultVariant);
        }
    }

    applyPageValues(variantId) {
        const variant = this.getVariants().find(v => v.id == variantId);
        if (!variant) return;

        const selectedVariants = this.getAllSelectedVariants(variantId);

        const selectedVariantMetafields = this.getVariantMetafields().filter(m => selectedVariants.some(v => v.id == m.variant_id));
        // Example of applying metafield values to the page:
        if (variant.available) {
            const offerMetafields = selectedVariantMetafields.map(m => m.metafields.offer).filter(Boolean);
            const subtotalTextMetafields = selectedVariantMetafields.map(m => m.metafields.subtotal_text).filter(Boolean);
            const customerOfferElement = document.querySelector('.customer-offer');
            const subtotalTextElement = document.querySelector('#total-price-text');
            if (customerOfferElement && offerMetafields.length > 0) {
                customerOfferElement?.classList.remove('hidden');
                const firstOffer = offerMetafields[0];
                customerOfferElement.querySelector('.js-offer-title').innerText = firstOffer.title;
                customerOfferElement.querySelector('.js-offer-text').innerHTML = firstOffer.text;
            }
            else if (customerOfferElement) {
                customerOfferElement.classList.add('hidden');
            }

            if (subtotalTextElement && subtotalTextMetafields.length > 0) {
                subtotalTextElement.classList.remove('hidden');
                const firstSubtotalText = subtotalTextMetafields[0];
                subtotalTextElement.innerHTML = firstSubtotalText;
            } else if (subtotalTextElement) {
                subtotalTextElement.innerHTML = '';
                subtotalTextElement.classList.add('hidden');
            }
        }
    }

    getAllSelectedVariants(variantId) {
        const variantIds = [variantId];
        const products = this.getProducts();
        const parentProductId = products.find(p => p.variants.some(v => v.id == variantId))?.id;
        document.querySelectorAll('.js-variant-id').forEach(el => {
            const variantId = el.value;
            if (el.dataset.productId == parentProductId) return;
            if (variantId) variantIds.push(variantId);
        });

        const variantsById = products.map(p => p.variants || []).flat().filter(v => variantIds.includes(v.id.toString()));

        const variantMetafields = this.getVariantMetafields();
        variantsById.forEach(variant => {
            variant.metafields = variantMetafields.find(m => m.variant_id == variant.id) || {};
        });
        console.log('All variants by ID:', variantsById);
        return variantsById;
    }

    onOptionChange(event) {
        this.updateUrl(event?.target?.value);
        this.updateFormValues(event?.target?.value);
        this.renderVariantUpdates(event?.target?.value);
        this.applyHiddenOptionsUI();
        this.applyPageValues(event?.target?.value);
        this.selectVariantImage(event?.target?.value);
        if (this.productIndex === null) this.syncBundleChildrenToParent();
    }

    selectVariantImage(variantId) {
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const $gallery = $('.product-detail .gallery');
        $gallery.trigger('variantImageSelected', variant);
    }

    // this is a nondestructive method of changing query params
    getNewUrl(variantId) {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const variantParamName = this.productIndex ? `variant_${this.productIndex}` : 'variant';
        params.set(variantParamName, variantId);
        return `${url.pathname}?${params.toString()}`;
    }

    updateUrl(variantId) {
        if (this.dataset.updateUrl === "false") return;
        const url = this.getNewUrl(variantId);
        window.history.replaceState(
            {},
            "",
            url
        );
    }
    updateFormValues(variantId) {
        const variantInput = this.querySelector(`.js-variant-id`);
        console.log('Updating variant input to variant ID:', variantId, variantInput);
        if (variantInput) {
            variantInput.value = variantId;
            const event = new Event('change');
            variantInput.dispatchEvent(event);
        }
    }

    renderVariantUpdates(variantId) {
        const url = `${this.dataset.url}?view=phone&variant=${variantId}&section_id=${this.dataset.sectionId}`
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                this.renderTargets.forEach(target => {
                    this.reRenderSection(`#${target}`, html);
                });
                this.updateTotalPrice();
                this.applyHiddenOptionsUI();
                this.selectVariantImage(variantId);
            });
    }

    getCheckedInputForOption(optionName) {
        return this.querySelector(`input[type="radio"][name="${CSS.escape(optionName)}"]:checked`);
    }

    setSubtitleText(subtitleId, text) {
        if (!subtitleId) return;
        const el = this.querySelector(`#${CSS.escape(subtitleId)}`);
        if (!el) return;
        el.textContent = text;
    }

    applyHiddenOptionsUI() {
        const hidden = this.getHiddenOptionNames();
        if (!hidden.length) return;

        hidden.forEach(optionName => {
            const optionRow = this.querySelector(`.product-option[data-option-name="${CSS.escape(optionName)}"]`);
            if (optionRow) optionRow.style.display = 'none';

            const option = this.getCheckedInputForOption(optionName);
            const subtitleId = option?.dataset.subtitleId;
            const selectedText = `${optionName}: ${option?.dataset.optionValue || ''}`;
            if (selectedText) this.setSubtitleText(subtitleId, selectedText);
        });
    }

    updateTotalPrice() {
        const priceElements = document.querySelectorAll('.js-product-price');
        let totalPrice = 0;
        priceElements.forEach(el => {
            console.log('Found price element with text:', el.textContent);
            const price = parseFloat(el.textContent.replace(/[^0-9.-]+/g, ""));
            if (!isNaN(price)) {
                totalPrice += price;
            }
        });
        const totalPriceElements = document.querySelectorAll('.js-total-price');
        console.log('Updating total price to:', totalPrice);
        console.log('Total price elements:', totalPriceElements);
        totalPriceElements.forEach(el => {
            el.textContent = new Intl.NumberFormat(window.Shopify.locale, { style: 'currency', currency: window.Shopify.currency.active }).format(totalPrice);
        });
    }

    reRenderSection(selector, html) {
        const existing = document.querySelector(selector);
        const fresh = html.querySelector(selector);
        if (!existing || !fresh) return null;
        
        if(fresh.dataset.reconnect == "true") {
            existing.replaceWith(fresh.cloneNode(true));
            return;
        }
        existing.innerHTML = fresh.innerHTML;
    }
}
customElements.define("product-options", ProductOptions);
